import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Copy, Edit2, X, Save, MapPin } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import { adminService } from '../../../services/adminService';
import { toast } from 'sonner';
import { statusConfig, safeDate, NON_EDITABLE_STATUSES, GATED_STATUS, SETTABLE_STATUS_ENTRIES } from './ordersConstants';
import { editBlockedMessage } from '../../../lib/orderEditPolicy';
import { formatShortDay } from '../../../lib/overOrder';
import { OrderInsightsSection } from './OrderInsightsSection';
import { DeliveryProofSection } from './DeliveryProofSection';
import { AdvanceInvoiceDialog } from './AdvanceInvoiceDialog';
import { AdvanceInvoiceBadge } from './AdvanceInvoiceBadge';
import { LocationLine } from '../../../components/LocationLine';
import { OrderLocationDialog } from './OrderLocationDialog';

// An admin may invoice in advance only while the order is still in flight and
// has no invoice yet (mirrors the server-side eligibility rules).
const ADVANCE_ELIGIBLE_STATUSES = new Set([
  'submitted', 'confirmed', 'in_production', 'ready_for_delivery', 'out_for_delivery',
]);

const newUid = (seed) =>
  (typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${seed}-${Date.now()}-${Math.random().toString(36).slice(2)}`);

const buildEditForm = (o) => ({
  items: (o.items || []).map(item => ({ ...item, _uid: item._uid || newUid(item.product_id || 'item') })),
  delivery_address: o.delivery_address || '',
  delivery_date: o.delivery_date?.split('T')[0] || '',
  delivery_time_window: o.delivery_time_window || '',
  po_number: o.po_number || '',
  order_notes: o.order_notes || '',
});

export const OrderDetailDialog = ({
  open,
  onOpenChange,
  order,
  products,
  availableProducts,
  startInEdit,
  onUpdateStatus,
  onReorder,
  onOrderSaved,
  onEditCustom,
}) => {
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const [advanceOpen, setAdvanceOpen] = useState(false);
  const navigate = useNavigate();
  const [locationOpen, setLocationOpen] = useState(false);

  const canInvoiceNow = !!order
    && !order.is_custom
    && !order.invoice_id
    && ADVANCE_ELIGIBLE_STATUSES.has(order.status);
  const isAdvanceInvoiced = order?.billing_timing === 'advance' && !!order?.invoice_id;

  // The orders list serves flat over-ordering fields; a freshly-read order
  // carries the nested record. Read whichever is present.
  const heldLineCount = order?.over_cap_line_count ?? (order?.over_cap?.lines?.length || 0);
  const heldDeadline = order?.approval_deadline || order?.over_cap?.approval_deadline || null;
  const heldReason = order?.over_cap_reason_label || order?.over_cap?.reason_label || null;

  useEffect(() => {
    if (open && order) {
      if (startInEdit) {
        setEditForm(buildEditForm(order));
        setEditMode(true);
      } else {
        setEditMode(false);
        setEditForm(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, order?.id, startInEdit]);

  const handleEditOrder = () => {
    // A custom order is corrected on the form it was booked on — it has no
    // client account, and its lines can be invented one-offs.
    if (order.is_custom) {
      onOpenChange(false);
      if (onEditCustom) onEditCustom(order);
      return;
    }
    setEditForm(buildEditForm(order));
    setEditMode(true);
  };

  const handleUpdateItem = (index, field, value) => {
    setEditForm(prev => {
      const newItems = [...prev.items];
      newItems[index] = { ...newItems[index], [field]: value };
      if (field === 'quantity' || field === 'unit_price') {
        const qty = field === 'quantity' ? parseFloat(value) : newItems[index].quantity;
        const price = field === 'unit_price' ? parseFloat(value) : newItems[index].unit_price;
        newItems[index].subtotal = qty * price;
      }
      return { ...prev, items: newItems };
    });
  };

  const handleRemoveItem = (index) => {
    setEditForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  const handleAddItem = () => {
    setEditForm(prev => ({
      ...prev,
      items: [...prev.items, {
        _uid: newUid('new'),
        product_id: '',
        product_name: '',
        pack_size: '',
        quantity: 1,
        unit_price: 0,
        subtotal: 0,
      }],
    }));
  };

  const calculateTotal = () => (editForm ? editForm.items.reduce((sum, item) => sum + (item.subtotal || 0), 0) : 0);

  const handleSaveOrder = async () => {
    setSavingOrder(true);
    try {
      const total = editForm.items.reduce((sum, item) => sum + item.subtotal, 0);
      const cleanItems = editForm.items.map(({ _uid, ...rest }) => rest);
      const updateData = {
        items: cleanItems,
        delivery_address: editForm.delivery_address,
        delivery_date: editForm.delivery_date,
        delivery_time_window: editForm.delivery_time_window,
        po_number: editForm.po_number,
        order_notes: editForm.order_notes,
        total_amount: total,
      };
      await adminService.updateOrder(order.id, updateData);
      onOrderSaved({ ...order, ...updateData });
      setEditMode(false);
      toast.success('Order updated successfully');
    } catch (error) {
      console.error('Error saving order:', error);
      toast.error('Failed to save order');
    } finally {
      setSavingOrder(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        {order && (
          <>
            <DialogHeader>
              <div className="flex items-center justify-between">
                <DialogTitle className="flex items-center gap-2">
                  {order.order_number || 'Order Details'}
                  {order.source === 'recurring' && (
                    <span className="text-xs bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full font-normal" data-testid="detail-recurring-badge">Recurring</span>
                  )}
                  <Badge className={statusConfig[order.status]?.color || 'bg-gray-100 text-gray-800'}>
                    {statusConfig[order.status]?.label || 'Unknown'}
                  </Badge>
                </DialogTitle>
                <DialogDescription className="sr-only">{statusConfig[order.status]?.label || 'Unknown'} dialog. Use the form below to continue.</DialogDescription>
                {!editMode ? (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { onOpenChange(false); onReorder(order); }}
                      data-testid="reorder-btn"
                    >
                      <Copy className="h-4 w-4 mr-2" />
                      Re-order
                    </Button>
                    {!NON_EDITABLE_STATUSES.has(order.status) ? (
                      <Button variant="outline" size="sm" onClick={handleEditOrder} data-testid="edit-order-btn">
                        <Edit2 className="h-4 w-4 mr-2" />
                        Edit Order
                      </Button>
                    ) : (
                      <p className="max-w-[18rem] text-xs text-gray-500 self-center" data-testid="edit-order-blocked">
                        {editBlockedMessage(order.status)}
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => { setEditMode(false); setEditForm(null); }}>
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      className="bg-amber-500 hover:bg-amber-600"
                      onClick={handleSaveOrder}
                      disabled={savingOrder}
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {savingOrder ? 'Saving...' : 'Save'}
                    </Button>
                  </div>
                )}
              </div>
            </DialogHeader>

            <div className="space-y-4 mt-4">
              {/* Advance invoice provenance */}
              {!editMode && isAdvanceInvoiced && <AdvanceInvoiceBadge order={order} />}

              {/* Client & Delivery Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase mb-1">Client</p>
                  <p className="font-medium">{order.client_name || 'Unknown'}</p>
                  <LocationLine
                    name={order.location_name}
                    source={order.location_name_source}
                    clientName={order.client_name}
                    className="mt-1"
                    testId={`order-detail-location-${order.id}`}
                  />
                  {!editMode && !order.is_custom && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="mt-1 h-7 px-1 text-xs text-amber-700 hover:text-amber-800"
                      onClick={() => setLocationOpen(true)}
                      data-testid="edit-order-location-btn"
                    >
                      <MapPin className="mr-1 h-3.5 w-3.5" /> Edit delivery location
                    </Button>
                  )}
                </div>
                {editMode ? (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase mb-1">Delivery Date</p>
                    <Input
                      type="date"
                      value={editForm?.delivery_date || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, delivery_date: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                ) : (
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500 uppercase mb-1">Delivery</p>
                    <p className="font-medium">
                      {order.delivery_date
                        ? safeDate(order.delivery_date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
                        : 'N/A'}
                    </p>
                    <p className="text-sm text-gray-600">{order.delivery_time_window || ''}</p>
                  </div>
                )}
              </div>

              {/* Delivery Address */}
              {editMode ? (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <Label className="text-xs text-gray-500 uppercase">Delivery Address</Label>
                  <Textarea
                    value={editForm.delivery_address}
                    onChange={(e) => setEditForm(prev => ({ ...prev, delivery_address: e.target.value }))}
                    className="mt-1"
                    rows={2}
                  />
                </div>
              ) : order.delivery_address && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 uppercase mb-1">Delivery Address</p>
                  <p className="text-sm">{order.delivery_address}</p>
                </div>
              )}

              {/* Order Items */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <p className="text-sm font-medium text-gray-700">Order Items</p>
                  {editMode && (
                    <Button variant="outline" size="sm" onClick={handleAddItem}>
                      + Add Item
                    </Button>
                  )}
                </div>
                <div className="space-y-2">
                  {(editMode ? editForm.items : order.items)?.map((item, idx) => (
                    <div key={item._uid || item.product_id || `${item.product_name}-${idx}`} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                      {editMode ? (
                        <>
                          <div className="flex-1">
                            <Select
                              value={item.product_id}
                              onValueChange={(value) => {
                                const product = products.find(p => p.id === value);
                                if (product) {
                                  handleUpdateItem(idx, 'product_id', value);
                                  handleUpdateItem(idx, 'product_name', product.name);
                                  handleUpdateItem(idx, 'pack_size', product.pack_size);
                                  handleUpdateItem(idx, 'unit_price', product.wholesale_price);
                                  handleUpdateItem(idx, 'subtotal', item.quantity * product.wholesale_price);
                                }
                              }}
                            >
                              <SelectTrigger className="w-full">
                                <SelectValue placeholder="Select product" />
                              </SelectTrigger>
                              <SelectContent>
                                {availableProducts.map(p => (
                                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="w-20">
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => handleUpdateItem(idx, 'quantity', parseInt(e.target.value) || 1)}
                              placeholder="Qty"
                            />
                          </div>
                          <div className="w-24">
                            <Input
                              type="number"
                              step="0.01"
                              value={item.unit_price}
                              onChange={(e) => handleUpdateItem(idx, 'unit_price', parseFloat(e.target.value) || 0)}
                              placeholder="Price"
                            />
                          </div>
                          <div className="w-24 text-right font-medium">
                            ${(item.subtotal || 0).toFixed(2)}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500"
                            onClick={() => handleRemoveItem(idx)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <div className="flex-1">
                            <p className="font-medium">{item.product_name}</p>
                            <p className="text-sm text-gray-500">{item.pack_size}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm">{item.quantity} × ${(item.unit_price || 0).toFixed(2)}</p>
                            <p className="font-medium">${(item.subtotal || 0).toFixed(2)}</p>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>

                <div className="flex justify-between items-center pt-3 border-t mt-3">
                  <p className="font-semibold">Total</p>
                  <p className="text-xl font-bold text-amber-600">
                    ${editMode ? calculateTotal().toFixed(2) : (order.total_amount || 0).toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Order Insights (historical sell-through) */}
              {!editMode && <OrderInsightsSection orderId={order.id} active={open && !editMode} />}

              {/* Delivery Proof (photos, pickup count, outcome, send to client) */}
              {!editMode && !order.is_custom && (
                <DeliveryProofSection
                  orderId={order.id}
                  orderStatus={order.status}
                  active={open && !editMode}
                />
              )}

              {/* PO & Notes */}
              {editMode ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-gray-500 uppercase">PO Number</Label>
                    <Input
                      value={editForm.po_number}
                      onChange={(e) => setEditForm(prev => ({ ...prev, po_number: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500 uppercase">Time Window</Label>
                    <Select
                      value={editForm.delivery_time_window}
                      onValueChange={(v) => setEditForm(prev => ({ ...prev, delivery_time_window: v }))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue placeholder="Select time" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="6:00 AM - 8:00 AM">6:00 AM - 8:00 AM</SelectItem>
                        <SelectItem value="7:00 AM - 9:00 AM">7:00 AM - 9:00 AM</SelectItem>
                        <SelectItem value="8:00 AM - 10:00 AM">8:00 AM - 10:00 AM</SelectItem>
                        <SelectItem value="9:00 AM - 11:00 AM">9:00 AM - 11:00 AM</SelectItem>
                        <SelectItem value="10:00 AM - 12:00 PM">10:00 AM - 12:00 PM</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Label className="text-xs text-gray-500 uppercase">Order Notes</Label>
                    <Textarea
                      value={editForm.order_notes}
                      onChange={(e) => setEditForm(prev => ({ ...prev, order_notes: e.target.value }))}
                      className="mt-1"
                      rows={2}
                    />
                  </div>
                </div>
              ) : (order.po_number || order.order_notes) && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  {order.po_number && (
                    <p className="text-sm"><span className="font-medium">PO:</span> {order.po_number}</p>
                  )}
                  {order.order_notes && (
                    <p className="text-sm"><span className="font-medium">Notes:</span> {order.order_notes}</p>
                  )}
                </div>
              )}

              {/* Status Update (when not editing). A held order leaves this
                  state through the approval queue, not a dropdown. */}
              {!editMode && order.status === GATED_STATUS ? (
                <div className="pt-4 border-t" data-testid="order-detail-held-notice">
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3">
                    <p className="text-sm font-semibold text-amber-900">
                      Held above this client's own best day
                    </p>
                    <p className="text-xs text-amber-800/90 mt-1">
                      {heldLineCount} line{heldLineCount === 1 ? '' : 's'} above the cap.
                      {heldDeadline ? ` Decide by ${formatShortDay(heldDeadline) || heldDeadline}.` : ''}
                      {heldReason ? ` Their reason: ${heldReason}.` : ''}
                      {' '}Nothing reaches the kitchen until it is released.
                    </p>
                    <Button
                      size="sm"
                      className="mt-2 bg-orange-600 hover:bg-orange-700"
                      onClick={() => { onOpenChange(false); navigate('/admin/over-ordering'); }}
                      data-testid="order-detail-review-approval-btn"
                    >
                      Review in the approval queue
                    </Button>
                  </div>
                </div>
              ) : !editMode && (
                <div className="flex items-center justify-between pt-4 border-t">
                  <p className="text-sm font-medium">Update Status:</p>
                  <Select
                    value={order.status}
                    onValueChange={(value) => onUpdateStatus(order.id, value)}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SETTABLE_STATUS_ENTRIES.map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Exception path — deliberately set apart from the routine
                  status actions above. */}
              {!editMode && canInvoiceNow && (
                <div className="pt-4 border-t border-dashed">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                    <div className="flex-1">
                      <p className="text-xs uppercase tracking-wide text-gray-400">Exception</p>
                      <p className="text-sm text-gray-600">
                        Bill this order before delivery so the client can pay in advance.
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="min-h-[44px] border-slate-300 text-slate-700 hover:bg-slate-100"
                      onClick={() => setAdvanceOpen(true)}
                      data-testid="invoice-now-btn"
                    >
                      Invoice Now (Advance)
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <AdvanceInvoiceDialog
              open={advanceOpen}
              onOpenChange={setAdvanceOpen}
              order={order}
              onIssued={(result) => onOrderSaved({
                ...order,
                invoice_id: result.invoice_id,
                invoice_number: result.invoice_number,
                billing_timing: 'advance',
              })}
            />

            <OrderLocationDialog
              open={locationOpen}
              onOpenChange={setLocationOpen}
              orderId={order.id}
              onSaved={(result) => onOrderSaved({
                ...order,
                location_name: result.location_name,
                location_name_source: result.location_name_source,
                delivery_address: result.delivery_address,
              })}
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
